import React from 'react';
import { Box } from '@chakra-ui/react';
import { useAppData } from '../store/dataStore';
import {
  FaBook,
  FaLightbulb,
  FaUsers,
  FaTrophy,
  FaChartLine,
  FaClock,
  FaCode,
  FaShieldAlt,
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Helmet } from 'react-helmet-async';
import { useAnalytics } from '../hooks/useAnalytics';
import {
  HeroSection,
  CurriculumSection,
  FAQSection,
  FinalCTA,
} from '../components/beginnerTrackComponents';
import {
  CollegeProjectHero,
  CollegeProjectBenefits,
  CollegeProjectAcademicProposal,
  CollegeProjectWhyNeeded,
  CollegeProjectCTA,
} from '../components/collegeproject/collegeProjectComponents';

const CollegeProjectPage = () => {
  const { appData } = useAppData();
  const { studentsCount, courseCollege } = appData;
  const { trackEvent } = useAnalytics();

  // -------- PAGE DATA --------
  const pageData = {
    hero: {
      badge: 'For College Students',
      title: 'Master AI/ML in 12 Weeks - Build a Real-World Project',
      description:
        'Learn AI, Machine Learning, and LLM development while building a portfolio project that gets you noticed by top companies.',      
    },

    benefits: {
      title: 'Why AI Skills Matter for College Students Today',
      description:
        'In an AI-first world, college students need hands-on AI experience to stay competitive. This condensed 12-week track bridges the gap between classroom theory and industry-ready skills.',
      benefits: [
        {
          icon: FaLightbulb,
          title: 'Stay Ahead of Curriculum',
          description:
            'Most colleges teach AI slowly. Master practical skills that textbooks never cover.',
        },
        {
          icon: FaChartLine,
          title: 'Boost Your Resume',
          description:
            'Real projects and GitHub repos impress recruiters more than grades ever will.',
        },
        {
          icon: FaTrophy,
          title: 'Win Hackathons & Competitions',
          description:
            'Apply cutting-edge AI techniques to dominate your campus competitions.',
        },
        {
          icon: FaBook,
          title: 'Learn Industry-Ready Skills',
          description:
            'Everything taught is what companies are actually building right now.',
        },
      ],
    },

    curriculum: {
      title: 'Accelerated 12-Week AI Curriculum',
      description:
        'Half the time, full impact. Focused modules designed for college students with limited time.',
      phases: [
        {
          duration: '2 Weeks',
          title: 'Python & AI Fundamentals',
          desc: 'Master Python with AI focus • Variables to OOP • Error handling • API basics',
          projects: '4 mini Projects',
        },
        {
          duration: '1 Week',
          title: 'Machine Learning Essentials',
          desc: 'Real-world ML with Scikit-learn • Classification & Regression • Model evaluation',
          projects: '1 ML Project',
        },
        {
          duration: '1 Week',
          title: 'NLP & Prompt Engineering',
          desc: 'LLM Basics • Advanced Prompting • Text Processing • Transformers intro',
          projects: '1 NLP Project',
        },
        {
          duration: '2 Weeks',
          title: 'Full-Stack AI Web Development',
          desc: 'Flask & FastAPI REST APIs • React Basics • Build deployable web apps',
          projects: '2 Web Apps',
        },
        {
          duration: '2 Weeks',
          title: 'Generative AI Mastery',
          desc: 'GPT/Claude/LLaMA models • LangChain • RAG systems • Real AI products',
          projects: '2 GenAI Apps',
        },
        {
          duration: '1 Week',
          title: 'Cloud Deployment & DevOps',
          desc: 'Docker • AWS/Firebase deployment • CI/CD pipelines',
          projects: 'Live Deployment',
        },
        {
          duration: '1 Week',
          title: 'Databases & Dev Tools',
          desc: 'RDBMS & NoSQL • Git & GitHub • AI-assisted development',
          projects: 'Database App',
        },
        {
          duration: '2 Week',
          title: 'Capstone & Portfolio Launch',
          desc: 'Final project refinement • GitHub optimization • Interview prep',
          projects: '1 Major Capstone',
        },
      ],
    },

    academicProposal: {
      title: 'Your Capstone Project Idea: AI-Powered Academic Companion',
      description:
        'Build a real-world AI product that solves a genuine college problem',
      projectTitle: 'Smart Study Assistant with RAG & LLM Integration',
      overview:
        'An intelligent system that understands your course materials and provides personalized explanations, generates practice problems, and adapts to your learning style.',
      features: [
        {
          title: 'Smart Document Understanding',
          description: 'Upload lecture notes, textbooks, or papers. The system indexes and understands the content using vector embeddings.',
        },
        {
          title: 'Personalized Q&A',
          description: 'Ask questions in natural language. Get answers specifically grounded in your course materials with citations.',
        },
        {
          title: 'Practice Problem Generation',
          description: 'Auto-generate practice questions from your materials at different difficulty levels.',
        },
        {
          title: 'Learning Analytics',
          description: 'Track your progress, identify weak areas, and get personalized learning recommendations.',
        },
        {
          title: 'Multi-Format Support',
          description: 'Works with PDFs, images of handwritten notes, YouTube transcripts, and text documents.',
        },
        {
          title: 'Web & Mobile Interface',
          description: 'Clean web interface for desktop, accessible via mobile for on-the-go studying.',
        },
      ],
      techStack: 'Python • FastAPI • React.js • OpenAI/Claude APIs • Vector DB (Pinecone/Supabase) • Firebase',
      impact:
        'This capstone is interview-ready, demonstrates full-stack AI expertise, and solves a real problem that colleges face.',
    },

    whyNeeded: {
      title: 'Why This Track is Critical for College Students',
      description:
        'Traditional college education is falling behind the AI revolution. Here\'s why you need this now.',
      reasons: [
        {
          title: 'Accelerated Industry Adoption',
          reason:
            'Companies are hiring AI engineers aggressively. Waiting until graduation puts you behind your peers.',
        },
        {
          title: 'Hands-On vs Theory Gap',
          reason:
            'Your college teaches ML math; companies need you to ship AI products. This bridges that gap.',
        },
        {
          title: 'Portfolio > Grades',
          reason:
            'Recruiters care about what you\'ve built, not your GPA. Real projects open doors that grades can\'t.',
        },
        {
          title: 'Competitive Edge in Placements',
          reason:
            'Stand out in campus placements. Your GenAI project makes you the top candidate for top-tier companies.',
        },
        {
          title: 'Internship & Startup Opportunities',
          reason:
            'AI skills unlock premium internships and startup opportunities that don\'t advertise on campus.',
        },
        {
          title: 'Future-Proof Your Career',
          reason:
            'AI is reshaping every industry. Early adoption of these skills ensures you\'re never displaced.',
        },
        {
          title: 'Hackathon & Competition Dominance',
          reason:
            'Win your campus AI hackathons and competitions with real, production-grade AI knowledge.',
        },
        {
          title: 'Build Your Personal Brand',
          reason:
            'GitHub repos, deployed projects, and technical writing establish your credibility before graduation.',
        },
      ],
    },

    faq: {
      title: 'Questions About the College Track?',
      description: 'Everything you need to know.',
      items: [
        {
          q: 'Can I balance this with my college coursework?',
          a: 'Yes! This is designed for busy college students. 12 weeks, flexible scheduling, with recorded sessions for asynchronous learning. Most students complete it while maintaining their regular studies.',
        },
        {
          q: 'Do I need prior experience?',
          a: 'Basic programming knowledge helps, but not required. If you can write simple Python code, you\'re ready.',
        },
        {
          q: 'Will this help with college assignments?',
          a: 'Absolutely. The skills directly apply to your CS/AI courses, often giving you better grades in those subjects.',
        },
        {
          q: 'Can I use this for my final year project?',
          a: 'Yes! Many students build their college final projects using what they learn here, impressing their professors.',
        },
        {
          q: 'What if I fall behind?',
          a: '12-month access to all materials. Work at your own pace. Community support and instructor office hours available.',
        },
        {
          q: 'Will this help with internship/placement interviews?',
          a: 'This is specifically designed to make you shine in technical interviews and internship placements.',
        },
      ],
    },

    finalCTA: {
      title: 'Your AI Journey Starts Now',
      description: `Join college students who are building AI products while their peers are still learning theory. Don't graduate unprepared.`,
      buttons: [
        { text: 'Start Your AI Journey', href: '/assessment', variant: 'solid' },
        { text: 'Talk to a Mentor', href: '/contact', variant: 'outline' },
      ],
      footer: '✓ 15-day free trial ✓ Lifetime access ✓ Build real projects ✓ Get hired',
    },
  };

  // -------- EVENT HANDLERS --------
  const handleEnrollClick = (section) => {
    if (section === 'pricing') {
      trackEvent('college_project_cta_click', {
        page: 'college_project',
        section: 'pricing',
      });
    }
    console.log(`Enroll clicked from ${section}`);
  };

  const handleTalkToAdvisorClick = () => {
    trackEvent('college_project_advisor_click', {
      page: 'college_project',
      section: 'final_cta',
    });
    console.log('Talk to Advisor clicked');
  };

  // -------- PAGE RENDER --------
  return (
    <>
      <Helmet>
        <title>College Project Track | Master AI in 12 Weeks</title>
        <meta
          name="description"
          content="Master AI, Machine Learning, and Generative AI in 12 weeks while building a portfolio-ready capstone project. Perfect for college students."
        />
      </Helmet>

      <Box>
        <Navbar />

        <CollegeProjectHero
          data={pageData.hero}
          onEnrollClick={() => handleEnrollClick('hero')}
        />

        <CollegeProjectBenefits data={pageData.benefits} />

        <CurriculumSection data={pageData.curriculum} />

        <CollegeProjectAcademicProposal data={pageData.academicProposal} />

        <CollegeProjectWhyNeeded data={pageData.whyNeeded} />

        <FAQSection data={pageData.faq} />

        <FinalCTA
          data={pageData.finalCTA}
          onEnrollClick={() => handleEnrollClick('final')}
          onTalkToAdvisorClick={handleTalkToAdvisorClick}
        />

        <Footer />
      </Box>
    </>
  );
};

export default CollegeProjectPage;
